// screens/create-product-mgt/AddProduct.jsx
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Spinner, Card, Badge, Alert } from 'react-bootstrap';
import Select from "react-select";
import { TagsInput } from 'react-tag-input-component';
import makeAnimated from "react-select/animated";
import { toast } from 'react-toastify';
import axios from '../../api/axios';

const AddProduct = ({
    data, onNext, onBack, updateData,
    currentStep, isSubmitting, setIsSubmitting, setErrors, isProductCreated
}) => {
    const accessToken = localStorage.getItem('authToken');
    const productId = localStorage.getItem('productId');

    const [isLoading, setIsLoading] = useState(false);
    const [categoryList, setCategoryList] = useState([]);
    const [productTags, setProductTags] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [searchText, setSearchText] = useState('');

    const [selectedExcludedOutlets, setSelectedExcludedOutlets] = useState([]);
    const [selectedNature, setSelectedNature] = useState([]);
    const [selectedKeywords, setSelectedKeywords] = useState([]);
    const [displayPreview, setDisplayPreview] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [allowImageUpdate, setAllowImageUpdate] = useState(false);

    const [isNowEditForm, setIsNowEditForm] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [editSelectedExcludedOutlets, setEditSelectedExcludedOutlets] = useState([]);
    const [editSelectedNature, setEditSelectedNature] = useState([]);
    const [editSelectedKeywords, setEditSelectedKeywords] = useState([]);

    const animatedComponents = makeAnimated();
    const product = data.product || {};

    const isFreebie = (() => {
        const val = isNowEditForm ? editedData.isFreebieProduct : product.isFreebieProduct;
        return val === true || val === 'true';
    })();

    useEffect(() => {
        fetchCategoryList();
        fetchOutlets();
    }, []);

    useEffect(() => {
        if (searchText.length > 2) fetchProductTags(searchText);
    }, [searchText]);

    useEffect(() => {
        if (productId && isProductCreated) getEditingProductDetails(productId);
    }, [isProductCreated]);

    const fetchCategoryList = async () => {
        try {
            const response = await axios.get("masters/category/get", {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response?.data?.status === true) setCategoryList(response.data.data.categories);
        } catch {
            toast.error("Failed to load categories", { position: "top-right" });
        }
    };

    const fetchProductTags = async (text) => {
        try {
            const response = await axios.get(`/product/tags?search=${text}`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response?.data?.status === true) setProductTags(response.data.data.productTags);
        } catch { /* silently fail */ }
    };

    const fetchOutlets = async () => {
        try {
            const response = await axios.get("outlet", {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response?.data?.status === true) setOutlets(response.data.data.outlets);
        } catch {
            toast.error("Failed to load outlets", { position: "top-right" });
        }
    };

    const getEditingProductDetails = async (prodId) => {
        try {
            const response = await axios.get(`product?_id=${prodId}`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            const pd = response?.data?.data?.products;

            if (response?.data?.status === true && pd) {
                setIsNowEditForm(true);
                setEditedData({
                    _id: pd._id,
                    sequenceNumber: pd?.sequenceNumber || '',
                    name: pd.name || '',
                    description: pd.description || '',
                    hsn: pd.hsn || '',
                    taxPercent: pd.taxPercent || '',
                    makingTime: pd.makingTime || '',
                    mrp: pd.mrp || '',
                    status: pd?.status || '',
                    category: pd?.category?._id || '',
                    offerPrice: pd?.offerPrice || '',
                    foodType: pd?.foodType || '',
                    classType: pd?.classType || '',
                    productTagId: pd?.productTag?._id || '',
                    productTagName: pd?.productTag?.tagName || '',
                    isEventProduct: pd?.isEventProduct || false,
                    isFreebieProduct: pd?.isFreebieProduct,
                    calories: pd?.calories || '',
                    weight: pd?.weight || '',
                    weightUnit: pd?.weightUnit || '',
                    orderType: pd?.orderType || 'ALL',
                    isNewProduct: pd?.isNewProduct || false,
                    productCost: pd?.productCost || 0,
                    displayImage: pd?.displayImage || '',
                    coverPicture: pd?.coverPicture || '',
                });

                if (pd.keywords) {
                    const tags = pd.keywords.split(',').filter(k => k.trim() !== '');
                    setEditSelectedKeywords(tags);
                    setSelectedKeywords(tags);
                }

                if (pd?.excludedOutletDetails?.length > 0) {
                    const mapped = pd.excludedOutletDetails.map(m => ({
                        value: m?._id,
                        label: `${m?.name}${m?.city ? ` (${m.city})` : ''}`
                    }));
                    setEditSelectedExcludedOutlets(mapped);
                    setSelectedExcludedOutlets(mapped);
                }

                if (pd?.productsNature?.length > 0) {
                    setEditSelectedNature([...pd.productsNature]);
                    setSelectedNature([...pd.productsNature]);
                }

                updateData('product', {
                    ...pd,
                    category: pd?.category?._id || '',
                    productTagId: pd?.productTag?._id || '',
                    productTagName: pd?.productTag?.tagName || '',
                    isFreebieProduct: pd?.isFreebieProduct || '',
                });
            }
        } catch {
            setIsNowEditForm(false);
            toast.error("Failed to load product details", { position: "top-right" });
        }
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "displayImage" && files[0]) {
            const file = files[0];
            setDisplayPreview(URL.createObjectURL(file));
            const reader = new FileReader();
            reader.onload = (ev) => {
                const img = new Image();
                img.onload = () => {
                    if (img.width === 400 && img.height === 400) {
                        updateData('product', { [name]: file });
                    } else {
                        toast.error(`Image must be 400×400 px. Got: ${img.width}×${img.height}`);
                        e.target.value = '';
                        setDisplayPreview(null);
                    }
                };
                img.src = ev.target.result;
            };
            reader.readAsDataURL(file);
            return;
        }

        if (name === "coverPicture" && files[0]) {
            setCoverPreview(URL.createObjectURL(files[0]));
            updateData('product', { [name]: files[0] });
            return;
        }

        if (isNowEditForm) setEditedData(prev => ({ ...prev, [name]: value }));
        updateData('product', { [name]: value });
    };

    const handleNextStep = async () => {
        const currentProduct = isNowEditForm ? editedData : product;
        const currentKeywords = isNowEditForm ? editSelectedKeywords : selectedKeywords;
        const currentSelectedOutlets = isNowEditForm ? editSelectedExcludedOutlets : selectedExcludedOutlets;
        const currentSelectedNature = isNowEditForm ? editSelectedNature : selectedNature;

        if (isFreebie) {
            currentProduct.mrp = 0;
            currentProduct.offerPrice = 0;
        }

        const requiredFields = [
            'sequenceNumber', 'name', 'category', 'classType', 'foodType',
            'hsn', 'taxPercent', 'weight', 'weightUnit', 'calories',
            'makingTime', 'orderType', 'isEventProduct', 'isNewProduct', 'isFreebieProduct',
        ];
        if (!isFreebie) requiredFields.push('mrp', 'offerPrice');

        const missing = requiredFields.filter(f => {
            const v = currentProduct[f];
            return v === undefined || v === null || v === '';
        });

        if (missing.length > 0) {
            toast.error(`Missing fields: ${missing.join(', ')}`, { position: "top-right" });
            return;
        }
        if (currentKeywords.length === 0) {
            toast.error("At least one keyword required", { position: "top-right" });
            return;
        }
        if (!isNowEditForm && !product?.displayImage) {
            toast.error("Upload product image (400×400 px)", { position: "top-right" });
            return;
        }

        setIsLoading(true);
        setIsSubmitting(true);

        try {
            const formData = new FormData();
            if (product.displayImage && typeof product.displayImage !== 'string') {
                formData.append("displayImage", product.displayImage);
            }
            if (product.coverPicture && typeof product.coverPicture !== 'string') {
                formData.append("coverPicture", product.coverPicture);
            }

            const fields = [
                'sequenceNumber', 'category', 'name', 'description', 'hsn', 'taxPercent',
                'makingTime', 'mrp', 'offerPrice', 'productCost', 'foodType', 'classType',
                'calories', 'weight', 'weightUnit', 'isEventProduct', 'isNewProduct',
                'isFreebieProduct', 'orderType',
            ];
            fields.forEach(f => {
                if (currentProduct[f] !== undefined && currentProduct[f] !== null) {
                    formData.append(f, currentProduct[f]);
                }
            });

            if (currentKeywords.length > 0) {
                const cleaned = currentKeywords.map(k => k.trim()).filter(Boolean);
                if (!cleaned.length) {
                    toast.error("Enter valid keywords", { position: "top-right" });
                    setIsLoading(false); setIsSubmitting(false);
                    return;
                }
                formData.append("keywords", cleaned.join(','));
            }

            if (currentProduct.productTagId) formData.append("productTag[productTagId]", currentProduct.productTagId);
            if (currentProduct.productTagName) formData.append("productTag[productTagName]", currentProduct.productTagName);

            currentSelectedOutlets?.forEach((o, i) => {
                if (o?.value) formData.append(`excludedOutlets[${i}]`, o.value);
            });

            const cleanedNature = (currentSelectedNature || []).map(t => t.trim()).filter(Boolean);
            cleanedNature.forEach((n, i) => formData.append(`productsNature[${i}]`, n));

            const headers = {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${accessToken}`
            };
            let response;

            if (isNowEditForm) {
                formData.append("_id", currentProduct._id);
                response = await axios.put("product", formData, { headers });
            } else {
                response = await axios.post("product", formData, { headers });
            }

            if (response?.data?.status === true) {
                const pid = response?.data?.data?.dbResponse?._id || currentProduct._id;
                localStorage.setItem("productId", pid);
                updateData('product', { ...currentProduct, _id: pid });
                toast.success(isNowEditForm ? "Product updated!" : "Product created!", { position: "top-right" });

                if (!isNowEditForm) {
                    setSelectedExcludedOutlets([]);
                    setSelectedKeywords([]);
                    setSelectedNature([]);
                    setDisplayPreview(null);
                    setCoverPreview(null);
                }
                onNext();
            } else {
                toast.error(response?.data?.error || "Failed to save product", { position: "top-right" });
            }
        } catch (error) {
            let msg = '';
            if (error?.response) {
                const s = error.response.status;
                const d = error.response.data;
                if (s === 406) msg = d?.error?.message || d?.data?.message || d?.error;
                else if (s === 412) msg = d?.data?.message;
                else if (s === 400) msg = d?.error;
                else if (s === 500) msg = d?.message || d?.error;
            }
            toast.error(msg || "Failed to save product", { position: "top-right", autoClose: 3000 });
        } finally {
            setIsLoading(false);
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
            if (!isNowEditForm) localStorage.removeItem("productId");
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isNowEditForm]);

    const getValue = (field) => {
        if (isNowEditForm) return editedData[field] !== undefined ? editedData[field] : '';
        return product[field] !== undefined ? product[field] : '';
    };

    const outletOptions = outlets.map(o => ({
        value: o._id,
        label: `${o.name}${o.city ? ` (${o.city})` : ''}${o.status !== "ACTIVE" ? ` — ${o.status}` : ''}`
    }));

    const disabledStyle = { background: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' };

    return (
        <div className="add-product-step" style={{ fontSize: '13px' }}>
            {isNowEditForm && (
                <Alert variant="warning" className="mb-3 py-2">
                    <small>✏️ You're editing an existing product. Changes will overwrite current data.</small>
                </Alert>
            )}

            {isFreebie && (
                <Alert variant="success" className="mb-3 py-2">
                    <small>🎁 Freebie product — MRP & Offer Price auto-set to ₹0.00</small>
                </Alert>
            )}

            {/* SECTION 1 — Basic Information */}
            <Card className="mb-3 border-0 shadow-sm">
                <Card.Body className="p-3">
                    <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginBottom: 12 }}>
                        Basic information
                    </p>
                    <Row className="g-2">
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Sequence No <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="number" size="sm" name="sequenceNumber" value={getValue('sequenceNumber')} onChange={handleChange} placeholder="10" />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Category <span className="text-danger">*</span></Form.Label>
                                <Form.Select size="sm" name="category" value={getValue('category')} onChange={handleChange}>
                                    <option value="">Select</option>
                                    {categoryList.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={5}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Product name <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="text" size="sm" name="name" value={getValue('name')} onChange={handleChange} placeholder="Enter product name" />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Making time <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="number" size="sm" name="makingTime" value={getValue('makingTime')} onChange={handleChange} placeholder="15 min" />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Description</Form.Label>
                                <Form.Control as="textarea" rows={2} size="sm" name="description" value={getValue('description')} onChange={handleChange} placeholder="Optional description" />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr className="my-3" />
                    <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginBottom: 12 }}>
                        Classification
                    </p>
                    <Row className="g-2">
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 small">Class type <span className="text-danger">*</span></Form.Label>
                                <Form.Select size="sm" name="classType" value={getValue('classType')} onChange={handleChange}>
                                    <option value="">Select</option>
                                    <option value="FOOD">Food</option>
                                    <option value="BEVERAGE">Beverage</option>
                                    <option value="COMBOS">Combo</option>
                                    <option value="DESSERTS">Dessert</option>
                                    <option value="EXTRAS">Extras</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Food type <span className="text-danger">*</span></Form.Label>
                                <Form.Select size="sm" name="foodType" value={getValue('foodType')} onChange={handleChange}>
                                    <option value="">Select</option>
                                    <option value="VEG">Veg</option>
                                    <option value="NON-VEG">Non-veg</option>
                                    <option value="VEGAN">Vegan</option>
                                    <option value="VEG/VEGAN">Veg/Vegan</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Order type <span className="text-danger">*</span></Form.Label>
                                <Form.Select size="sm" name="orderType" value={getValue('orderType') || "ALL"} onChange={handleChange}>
                                    <option value="ALL">All</option>
                                    <option value="DELIVERY">Delivery</option>
                                    <option value="PICKUP">Pick-up</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Outlet type <span className="text-danger">*</span></Form.Label>
                                <Form.Select size="sm" name="isEventProduct" value={getValue('isEventProduct')} onChange={handleChange}>
                                    <option value="">Select</option>
                                    <option value="false">Regular</option>
                                    <option value="true">Event</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Freebie? <span className="text-danger">*</span></Form.Label>
                                <Form.Select size="sm" name="isFreebieProduct" value={getValue('isFreebieProduct')} onChange={handleChange}>
                                    <option value="">Select</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">New badge <span className="text-danger">*</span></Form.Label>
                                <Form.Select size="sm" name="isNewProduct" value={getValue('isNewProduct')} onChange={handleChange}>
                                    <option value="" disabled>Select</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* SECTION 2 — Pricing & Media */}
            <Card className="mb-3 border-0 shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', margin: 0 }}>
                            Pricing & media
                        </p>
                        {isFreebie && (
                            <Badge bg="success" style={{ fontSize: 10 }}>🎁 Prices locked at ₹0</Badge>
                        )}
                    </div>
                    <Row className="g-2">
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">MRP (₹) {!isFreebie && <span className="text-danger">*</span>}</Form.Label>
                                <Form.Control type="number" step="0.01" size="sm" name="mrp" value={isFreebie ? '0.00' : getValue('mrp')} onChange={handleChange} placeholder="0.00" disabled={isFreebie} style={isFreebie ? disabledStyle : {}} />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Offer (₹) {!isFreebie && <span className="text-danger">*</span>}</Form.Label>
                                <Form.Control type="number" step="0.01" size="sm" name="offerPrice" value={isFreebie ? '0.00' : getValue('offerPrice')} onChange={handleChange} placeholder="0.00" disabled={isFreebie} style={isFreebie ? disabledStyle : {}} />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Cost (₹)</Form.Label>
                                <Form.Control type="number" step="0.01" size="sm" name="productCost" value={getValue('productCost') || 0} onChange={handleChange} placeholder="0.00" />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Tax % <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="number" step="0.01" size="sm" name="taxPercent" value={getValue('taxPercent')} onChange={handleChange} placeholder="5" />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">HSN <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="text" size="sm" name="hsn" value={getValue('hsn')} onChange={handleChange} placeholder="HSN code" />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Weight <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="number" step="0.01" size="sm" name="weight" value={getValue('weight')} onChange={handleChange} placeholder="0.00" />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Unit <span className="text-danger">*</span></Form.Label>
                                <Form.Select size="sm" name="weightUnit" value={getValue('weightUnit')} onChange={handleChange}>
                                    <option value="">Select</option>
                                    <option value="KG">Kilograms</option>
                                    <option value="GMS">Grams</option>
                                    <option value="LITRES">Litres</option>
                                    <option value="ML">Millilitres</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Calories <span className="text-danger">*</span></Form.Label>
                                <Form.Control type="number" size="sm" name="calories" value={getValue('calories')} onChange={handleChange} placeholder="kcal" />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Product image <span className="text-danger">*</span> <span style={{ fontSize: 10, color: '#9ca3af' }}>(400×400 px)</span></Form.Label>
                                {isNowEditForm && !allowImageUpdate ? (
                                    <div className="d-flex align-items-center gap-2">
                                        {editedData.displayImage && (
                                            <img src={editedData.displayImage} alt="Current" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />
                                        )}
                                        <Button variant="outline-secondary" size="sm" style={{ fontSize: 11 }} onClick={() => setAllowImageUpdate(true)}>Replace</Button>
                                    </div>
                                ) : (
                                    <>
                                        <Form.Control type="file" accept="image/*" size="sm" name="displayImage" onChange={handleChange} />
                                        {displayPreview && <img src={displayPreview} alt="Preview" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, marginTop: 4 }} />}
                                    </>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Cover image <span style={{ fontSize: 10, color: '#9ca3af' }}>optional</span></Form.Label>
                                <Form.Control type="file" accept="image/*" size="sm" name="coverPicture" onChange={handleChange} />
                                {coverPreview ? (
                                    <img src={coverPreview} alt="Cover" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, marginTop: 4 }} />
                                ) : isNowEditForm && editedData.coverPicture ? (
                                    <img src={editedData.coverPicture} alt="Current cover" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, marginTop: 4 }} />
                                ) : null}
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Product tag <span style={{ fontSize: 10, color: '#9ca3af' }}>optional — type to search</span></Form.Label>
                                <Form.Control type="text" size="sm" value={getValue('productTagName')} onChange={(e) => {
                                    const v = e.target.value;
                                    setSearchText(v);
                                    if (isNowEditForm) setEditedData(prev => ({ ...prev, productTagName: v }));
                                    updateData('product', { productTagName: v });
                                }} placeholder="Search tags…" />
                                {productTags.length > 0 && (
                                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 4, marginTop: 2, background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', maxHeight: 150, overflowY: 'auto', position: 'relative', zIndex: 100 }}>
                                        {productTags.map(tag => (
                                            <div key={tag._id} style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                                onClick={() => {
                                                    if (isNowEditForm) setEditedData(prev => ({ ...prev, productTagId: tag._id, productTagName: tag.tagName }));
                                                    updateData('product', { productTagId: tag._id, productTagName: tag.tagName });
                                                    setProductTags([]);
                                                    setSearchText('');
                                                }}>
                                                {tag.tagName}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* SECTION 3 — Tags & Distribution */}
            <Card className="mb-3 border-0 shadow-sm">
                <Card.Body className="p-3">
                    <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', marginBottom: 12 }}>
                        Tags & distribution
                    </p>
                    <Row className="g-2">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Excluded outlets <span style={{ fontSize: 10, color: '#9ca3af' }}>optional</span></Form.Label>
                                <Select
                                    components={animatedComponents}
                                    isMulti
                                    options={outletOptions}
                                    value={isNowEditForm ? editSelectedExcludedOutlets : selectedExcludedOutlets}
                                    onChange={(sel) => {
                                        if (isNowEditForm) setEditSelectedExcludedOutlets(sel);
                                        else setSelectedExcludedOutlets(sel);
                                    }}
                                    placeholder="Select outlets…"
                                    closeMenuOnSelect={false}
                                    styles={{
                                        control: p => ({ ...p, fontSize: 12, minHeight: 32 }),
                                        option: p => ({ ...p, fontSize: 12 }),
                                        multiValue: p => ({ ...p, fontSize: 11 }),
                                        menu: p => ({ ...p, fontSize: 12 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Product nature <span style={{ fontSize: 10, color: '#9ca3af' }}>optional</span></Form.Label>
                                <TagsInput
                                    value={isNowEditForm ? editSelectedNature : selectedNature}
                                    onChange={(tags) => {
                                        if (isNowEditForm) setEditSelectedNature(tags);
                                        else setSelectedNature(tags);
                                    }}
                                    placeHolder="e.g. Spicy, Bestseller"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="mb-1 fw-semibold small">Keywords <span className="text-danger">*</span> <span style={{ fontSize: 10, color: '#9ca3af' }}>press Enter after each</span></Form.Label>
                                <TagsInput
                                    value={isNowEditForm ? editSelectedKeywords : selectedKeywords}
                                    onChange={(tags) => {
                                        if (isNowEditForm) setEditSelectedKeywords(tags);
                                        else setSelectedKeywords(tags);
                                    }}
                                    placeHolder="e.g. burger, chicken"
                                    separators={['Enter', ',']}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="mt-2 d-flex justify-content-end">
                <Button variant="primary" size="sm" onClick={handleNextStep} disabled={isLoading || isSubmitting}>
                    {isLoading ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-1" />
                            {isNowEditForm ? 'Updating…' : 'Creating…'}
                        </>
                    ) : (
                        isNowEditForm ? 'Update & continue →' : 'Create & continue →'
                    )}
                </Button>
            </div>
        </div>
    );
};

export default AddProduct;